import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';

export const fetchCustomerProfile = createAsyncThunk(
  'customers/fetchCustomerProfile',
  async (phoneNumber, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const existingCustomer = state.customers.profiles[phoneNumber];
      const lastFetched = existingCustomer?.lastFetched;
      const cacheAge = Date.now() - (lastFetched || 0);
      
      if (lastFetched && cacheAge < 10 * 60 * 1000) {
        return existingCustomer;
      }
      
      const response = await axios.get(API_ENDPOINTS.CUSTOMER_PROFILE(phoneNumber));
      return { phoneNumber, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch customer profile');
    }
  }
);

export const updateCustomerProfile = createAsyncThunk(
  'customers/updateCustomerProfile',
  async ({ phoneNumber, updates }, { rejectWithValue }) => {
    try {
      const response = await axios.put(API_ENDPOINTS.CUSTOMER_PROFILE_UPDATE(phoneNumber), updates);
      return { phoneNumber, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update customer profile');
    }
  }
);

export const fetchCustomerOrderHistory = createAsyncThunk(
  'customers/fetchCustomerOrderHistory',
  async ({ mobileNumber, restaurantId }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const existingHistory = state.customers.orderHistory[`${mobileNumber}-${restaurantId}`];
      const lastFetched = existingHistory?.lastFetched;
      const cacheAge = Date.now() - (lastFetched || 0);
      
      if (lastFetched && cacheAge < 5 * 60 * 1000) {
        return existingHistory;
      }
      
      const response = await axios.get(API_ENDPOINTS.CUSTOMER_ORDER_HISTORY(restaurantId, mobileNumber, null));
      return { key: `${mobileNumber}-${restaurantId}`, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch order history');
    }
  }
);

export const addToFavorites = createAsyncThunk(
  'customers/addToFavorites',
  async ({ phoneNumber, menuItemId }, { rejectWithValue }) => {
    try {
      await axios.post(API_ENDPOINTS.CUSTOMER_ADD_FAVORITE(phoneNumber), { menuItemId });
      return { phoneNumber, menuItemId, success: true };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add to favorites');
    }
  }
);

export const removeFromFavorites = createAsyncThunk(
  'customers/removeFromFavorites',
  async ({ phoneNumber, menuItemId }, { rejectWithValue }) => {
    try {
      await axios.delete(API_ENDPOINTS.CUSTOMER_REMOVE_FAVORITE(phoneNumber), { data: { menuItemId } });
      return { phoneNumber, menuItemId, success: true };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove from favorites');
    }
  }
);

const initialState = {
  profiles: {},
  orderHistory: {},
  favorites: {},
  loading: false,
  error: null,
};

const customerSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCustomerData: (state, action) => {
      const phoneNumber = action.payload;
      delete state.profiles[phoneNumber];
      delete state.orderHistory[phoneNumber];
      delete state.favorites[phoneNumber];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomerProfile.fulfilled, (state, action) => {
        const { phoneNumber, data } = action.payload;
        state.profiles[phoneNumber] = {
          ...data,
          lastFetched: Date.now()
        };
      })
      .addCase(updateCustomerProfile.fulfilled, (state, action) => {
        const { phoneNumber, data } = action.payload;
        state.profiles[phoneNumber] = {
          ...data,
          lastFetched: Date.now()
        };
      })
      .addCase(fetchCustomerOrderHistory.fulfilled, (state, action) => {
        const { key, data } = action.payload;
        state.orderHistory[key] = {
          ...data,
          lastFetched: Date.now()
        };
      })
      .addCase(addToFavorites.fulfilled, (state, action) => {
        const { phoneNumber, menuItemId } = action.payload;
        if (!state.favorites[phoneNumber]) {
          state.favorites[phoneNumber] = [];
        }
        if (!state.favorites[phoneNumber].includes(menuItemId)) {
          state.favorites[phoneNumber].push(menuItemId);
        }
      })
      .addCase(removeFromFavorites.fulfilled, (state, action) => {
        const { phoneNumber, menuItemId } = action.payload;
        if (state.favorites[phoneNumber]) {
          state.favorites[phoneNumber] = state.favorites[phoneNumber].filter(id => id !== menuItemId);
        }
      });
  },
});

export const selectCustomerProfile = (state, phoneNumber) => state.customers.profiles[phoneNumber];
export const selectCustomerOrderHistory = (state, mobileNumber, restaurantId) => 
  state.customers.orderHistory[`${mobileNumber}-${restaurantId}`];
export const selectCustomerFavorites = (state, phoneNumber) => state.customers.favorites[phoneNumber] || [];

export const { clearError, clearCustomerData } = customerSlice.actions;
export default customerSlice.reducer;

